/**
 * Reusable Input Component
 * Based on BaseTextfield Vue component reference
 */

import { Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  label?: string;
  error?: string;
  hint?: string;
  helperText?: string;
  dense?: boolean;
  flat?: boolean;
  value?: string | number;
  onChange?: (value: string) => void;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  hint,
  helperText,
  dense = false,
  flat = false,
  value: externalValue,
  onChange,
  type = 'text',
  disabled = false,
  placeholder = '',
  maxLength,
  max,
  min,
  className = '',
  id,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState<string | number>(
    externalValue ?? '',
  );
  const inputId =
    id || `input-${label?.toLowerCase().replace(/\s+/g, '-') || 'field'}`;

  // Sync internal value with external value
  useEffect(() => {
    if (externalValue !== undefined) {
      setInternalValue(externalValue);
    }
  }, [externalValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue: string | number;
    if (type === 'number') {
      newValue = e.target.value === '' ? '' : Number(e.target.value) || 0;
    } else {
      newValue = e.target.value;
    }
    setInternalValue(newValue);
    onChange?.(newValue as string);
  };

  const inputClasses = [
    'input-field',
    dense && 'input-dense',
    flat && 'input-flat',
    error && 'input-error',
    disabled && 'input-disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="input-container">
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
        </label>
      )}
      <input
        id={inputId}
        type={type}
        disabled={disabled}
        value={internalValue}
        onChange={handleChange}
        className={inputClasses}
        maxLength={maxLength}
        max={max}
        min={min}
        placeholder={placeholder}
        {...props}
      />
      {(hint || helperText) && !error && (
        <span className="input-hint">
          {hint && <Info size={14} />}
          <span>{hint || helperText}</span>
        </span>
      )}
      {error && <span className="input-error-message">{error}</span>}
    </div>
  );
};
